import type {
  AdminAiScanBusinessObject,
  AdminAiScanCount,
  AdminAiScanFindingEntry,
  AdminAiScanJson,
  AdminAiScanObject,
  AdminAiScanReportFile,
  AdminAiScanReviewModule,
} from "../models/admin-ai-scans.models";
import {
  ADMIN_AI_SCAN_FINDING_FIELD_LABELS,
  ADMIN_AI_SCAN_SEVERITIES,
  ADMIN_AI_SCAN_SEVERITY_ALIASES,
  ADMIN_AI_SCAN_SEVERITY_LABELS,
} from "./admin-ai-scans.constants";

export const getAdminAiScanObject = (value: AdminAiScanJson | undefined): AdminAiScanObject =>
  value !== null && typeof value === "object" && !Array.isArray(value) ? value : {};

export const getAdminAiScanObjects = (value: AdminAiScanJson | undefined): AdminAiScanObject[] =>
  Array.isArray(value)
    ? value.filter(
        (item): item is AdminAiScanObject => item !== null && typeof item === "object" && !Array.isArray(item),
      )
    : [];

export const getAdminAiScanText = (value: AdminAiScanJson | undefined, fallback = ""): string =>
  typeof value === "string" || typeof value === "number" || typeof value === "boolean" ? String(value) : fallback;

export const getAdminAiScanStrings = (value: AdminAiScanJson | undefined): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];

// The original color and numeric risk stay visible alongside the readable severity label.
export const getAdminAiScanFindingSeverity = (finding: AdminAiScanObject): string => {
  const level = getAdminAiScanText(finding.issueLevel || finding.color || finding.severity).toLowerCase();
  const originalColor = ADMIN_AI_SCAN_SEVERITIES.find((color) => color === level);

  return originalColor ?? ADMIN_AI_SCAN_SEVERITY_ALIASES.get(level) ?? "unknown";
};

// Inventory owns order and labels. Unmatched scan reports remain visible after the known BOs.
// Keep multiple reports for one BO instead of silently replacing evidence with a last-file winner.
export const getAdminAiScanBusinessObjects = (
  reports: AdminAiScanReportFile[],
  featureReports: AdminAiScanReportFile[],
): AdminAiScanBusinessObject[] => {
  const entries = new Map<string, AdminAiScanBusinessObject>();

  featureReports.forEach((file) => {
    const meta = getAdminAiScanObject(file.report.report);
    const moduleId = getAdminAiScanText(meta.moduleId, file.path);
    getAdminAiScanObjects(file.report.tree).forEach((inventory) => {
      const businessObjectId = getAdminAiScanText(inventory.id, file.path);
      const key = `${moduleId}::${businessObjectId}`;
      entries.set(key, {
        key,
        moduleId,
        moduleLabel: getAdminAiScanText(meta.moduleName, moduleId),
        businessObjectId,
        label: getAdminAiScanText(inventory.label, businessObjectId),
        inventory,
        reports: [],
      });
    });
  });

  reports.forEach((file) => {
    const target = getAdminAiScanObject(file.report.target);
    const moduleId = getAdminAiScanText(target.moduleId, "Unassigned module");
    const businessObjectId = getAdminAiScanText(target.businessObjectId, file.path);
    const key = `${moduleId}::${businessObjectId}`;
    const entry: AdminAiScanBusinessObject = entries.get(key) ?? {
      key,
      moduleId,
      moduleLabel: moduleId,
      businessObjectId,
      label: getAdminAiScanText(target.businessObjectLabel, businessObjectId),
      inventory: null,
      reports: [],
    };
    entry.reports.push(file);
    entries.set(key, entry);
  });

  return Array.from(entries.values());
};

export const getAdminAiScanReviewStatus = (report: AdminAiScanObject): string =>
  getAdminAiScanText(
    getAdminAiScanObject(report.boReview).status || getAdminAiScanObject(report.summary).status,
    "STATUS_UNSPECIFIED",
  );

export const matchesAdminAiScanSearch = (value: unknown, query: string): boolean =>
  !query.trim() || JSON.stringify(value).toLowerCase().includes(query.trim().toLowerCase());

// Read each report's finding array once, without repeating findings for every affected feature.
// Missing and unrecognized severities stay visible after the known severity levels.
export const getAdminAiScanFindingEntries = (entries: AdminAiScanBusinessObject[]): AdminAiScanFindingEntry[] => {
  const findings = entries.flatMap((entry) =>
    entry.reports.flatMap((file) =>
      getAdminAiScanObjects(file.report.findings).map((finding, index) => ({
        key: JSON.stringify([entry.key, file.path, finding.id ?? "", index]),
        finding,
        source: {
          moduleId: entry.moduleId,
          moduleLabel: entry.moduleLabel,
          businessObjectId: entry.businessObjectId,
          businessObjectLabel: entry.label,
          file,
        },
      })),
    ),
  );
  const severityOrder: readonly string[] = [...ADMIN_AI_SCAN_SEVERITIES, "unknown"];

  return findings.sort((left, right) => {
    const difference =
      severityOrder.indexOf(getAdminAiScanFindingSeverity(left.finding)) -
      severityOrder.indexOf(getAdminAiScanFindingSeverity(right.finding));

    const scoreDifference =
      (getAdminAiScanFindingScore(right.finding) ?? -Infinity) -
      (getAdminAiScanFindingScore(left.finding) ?? -Infinity);

    return difference || scoreDifference || left.key.localeCompare(right.key);
  });
};

// Search the issue and its owning labels, rather than matching unrelated findings in the same report.
export const filterAdminAiScanFindingEntries = (
  entries: AdminAiScanFindingEntry[],
  query: string,
  moduleId: string,
  severity: string,
): AdminAiScanFindingEntry[] =>
  entries.filter(
    ({ finding, source }) =>
      (!moduleId || source.moduleId === moduleId) &&
      (!severity || getAdminAiScanFindingSeverity(finding) === severity) &&
      matchesAdminAiScanSearch(
        [
          finding,
          source.moduleId,
          source.moduleLabel,
          source.businessObjectId,
          source.businessObjectLabel,
          source.file.path,
        ],
        query,
      ),
  );

// Use plain section headings while leaving exact report keys available in the original JSON details.
export const getAdminAiScanFindingFieldLabel = (field: string): string => {
  const words = field.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/Ids\b/g, "IDs");

  return ADMIN_AI_SCAN_FINDING_FIELD_LABELS[field] ?? words.charAt(0).toUpperCase() + words.slice(1);
};

// Keep scores numeric for display and tie-breaking without inventing a score for incomplete findings.
export const getAdminAiScanFindingScore = (finding: AdminAiScanObject): number | null => {
  const value = finding.dangerScore ?? finding.riskScore ?? finding.score;
  const score = typeof value === "number" || (typeof value === "string" && value.trim()) ? Number(value) : NaN;

  return Number.isFinite(score) ? score : null;
};

export const getAdminAiScanFindingTitle = (finding: AdminAiScanObject): string =>
  getAdminAiScanText(finding.title, getAdminAiScanText(finding.id, "Finding"));

export const getAdminAiScanFindingExcerpt = (finding: AdminAiScanObject): string => {
  const text = ["explanation", "summary", "detail", "unsafeScenario", "unsafeOutcome", "attackScenario", "remediation"]
    .map((key) => getAdminAiScanText(finding[key]))
    .find((value) => value.trim());

  return text || "Open this finding to inspect its evidence and source report.";
};

export const getAdminAiScanSeverityCounts = (findings: AdminAiScanObject[]): AdminAiScanCount[] =>
  [...ADMIN_AI_SCAN_SEVERITIES, "unknown"]
    .map((level) => ({
      label: ADMIN_AI_SCAN_SEVERITY_LABELS[level],
      value: findings.filter((finding) => getAdminAiScanFindingSeverity(finding) === level).length,
      severity: level,
    }))
    .filter((count) => count.value > 0);

// Prepare the visible module rows once so their totals align with their displayed business objects.
export const getAdminAiScanReviewModules = (
  entries: AdminAiScanBusinessObject[],
  severity = "",
): AdminAiScanReviewModule[] => {
  const modules = new Map<string, AdminAiScanReviewModule>();

  entries.forEach((entry) => {
    const module: AdminAiScanReviewModule = modules.get(entry.moduleId) ?? {
      id: entry.moduleId,
      label: entry.moduleLabel,
      entries: [],
      findingsCount: 0,
    };
    module.entries.push(entry);
    module.findingsCount += entry.reports.reduce(
      (count, file) =>
        count +
        getAdminAiScanObjects(file.report.findings).filter(
          (finding) => !severity || getAdminAiScanFindingSeverity(finding) === severity,
        ).length,
      0,
    );
    modules.set(entry.moduleId, module);
  });

  return Array.from(modules.values());
};
